

import { useInfiniteQuery } from "react-query";
import { StaticVariables } from "../../../../Utils/StaticVariables";
import { supabase } from "../../../../config/supabaseClient";
import { useMemo, useCallback } from "react";
import { reactQueryParams } from "../../../../Utils/StayUpToDate/RenderComponent";
import { useContentBankRevision } from "../../../../Utils/query/contentBankRevision";
import { filterOutProductCatalogPosts } from "./filterUtils";

/**
 * Load content with pagination support
 */

export function useLoadQueryFeed(
  username: any,
  user_id: any,
  queryTerm: any,
  showSocials: any[],
  contentOrder: any,
  isShopLinks: any,
  removedPostIds: any,
  name: any,
 
  order?: any,
  customOrderPostIds?: any,
  activeFilter?: any,
) {
  const POSTS_PER_PAGE = 25;
  const contentBankRevision = useContentBankRevision(user_id);

  // Memoize static variables to prevent re-fetching
  const { connectingWords, SEARCH_EXCEPTIONS } = useMemo(
    () => StaticVariables(),
    []
  );

  // Memoize normalized query term
  const normalizedQuery = useMemo(
    () => queryTerm?.toLowerCase().replace(/\s+/g, " ").trim(),
    [queryTerm]
  );

  // Normalize removed post IDs to array for SQL
  const normalizedRemovedPostIds = useMemo(() => {
    if (!removedPostIds || removedPostIds === "") return [];
    if (Array.isArray(removedPostIds)) {
      return removedPostIds.map(String).filter(Boolean);
    }
    if (typeof removedPostIds === "string") {
      return removedPostIds.split(",").map((id: string) => id.trim()).filter(Boolean);
    }
    return [];
  }, [removedPostIds]);

  // Serialize array deps for query key so new array references don't trigger refetches
  const removedPostIdsKey = useMemo(() => {
    if (!normalizedRemovedPostIds.length) return "";
    return [...normalizedRemovedPostIds].sort().join(",");
  }, [normalizedRemovedPostIds]);

  const customOrderKey = useMemo(() => {
    if (!Array.isArray(customOrderPostIds) || customOrderPostIds.length === 0) {
      return "";
    }
    return customOrderPostIds
      .map((id: any) => String(id).trim())
      .filter((id: string) => id !== "")
      .join(",");
  }, [customOrderPostIds]);

  const normalizedCustomOrder = useMemo(() => {
    if (!customOrderKey) return [];
    return customOrderKey.split(",");
  }, [customOrderKey]);

  // Memoize query processing logic
  const processQuery = useCallback(
    (query: string) => {
      let words = query.split(" ");
      let localContentOrder = contentOrder;

      // Handle single word with 's' suffix
      if (words.length === 1 && !SEARCH_EXCEPTIONS.includes(query)) {
        const suffixRegex = /(s)$/i;
        if (suffixRegex.test(words[0])) {
          words = [words[0].replace(suffixRegex, "")];
        }
      }

      // Handle shop/buy keywords
      if (words.includes("shop") || words.includes("buy")) {
        localContentOrder = "shoppable";
        words = words.filter(
          (word: any) => word !== "shop" && word !== "buy"
        );
      }

      // Filter connecting words
      const filteredWords = words.filter(
        (word: any) => !connectingWords.includes(word.toLowerCase())
      );

      const singleWords = filteredWords.map((word: any) => `%%${word}%%`);
      const result = filteredWords.join(" ");
      const pgQuery = result.toLowerCase().replace(/\s+/g, " & ");

      return {
        pgQuery,
        singleWords,
        localContentOrder: localContentOrder.split(" ")[0].toLowerCase(),
      };
    },
    [contentOrder, connectingWords, SEARCH_EXCEPTIONS]
  );

  // Optimized fetch function with pagination
  const getPaginatedFeed = useCallback(
    async ({ pageParam = 1 } = {}) => {
      if (!normalizedQuery) {
        return { data: [], total_count: 0, originalDataLength: 0, post_ids: [] };
      }

      const { pgQuery, singleWords, localContentOrder } =
        processQuery(normalizedQuery);

      // Calculate offset for pagination
      const offset = (pageParam - 1) * POSTS_PER_PAGE;

      // Choose RPC function based on isShopLinks
      const rpcFunction = isShopLinks ? "query_based_shop" : "query_based_cont";

      const payloa = {
        query_string: pgQuery,
        userid: user_id,
        username: username,
        socials: showSocials,
        content_order: localContentOrder,
        single: [`%%${normalizedQuery}%%`],
        post_order: order,
        custom_order: normalizedCustomOrder,
        limit_num: POSTS_PER_PAGE,
        offset_num: offset,
        removed_post_ids: normalizedRemovedPostIds,
      }

      const { data: searchResult, error } = await supabase.rpc(rpcFunction, {
        query_string: pgQuery,
        userid: user_id,
        username: username,
        socials: showSocials,
        content_order: localContentOrder,
        single: [`%%${normalizedQuery}%%`],
        post_order: order,
        custom_order: normalizedCustomOrder,
        limit_num: POSTS_PER_PAGE,
        offset_num: offset,
        removed_post_ids: normalizedRemovedPostIds, // Pass to SQL - all filtering happens there
      });

      if (error) {
        throw new Error(error.message);
      }

      // Extract data, total, and post_ids from RPC response
      // SQL RPC returns: { data: Array, total: number, post_ids: Array }
      // - data: Array of posts for current page (after all SQL filtering including removed_post_ids)
      // - total: Total count of all filtered posts (calculated using COUNT(*) OVER() in FilteredPosts CTE)
      // - post_ids: All post_ids from FilteredPosts (not just current page) - used for ordering reference
      // All filtering (including removed_post_ids) is now done in SQL - no client-side filtering needed
      const responseData = searchResult?.data || [];
      const totalCount = searchResult?.total || 0;
      const allPostIds = searchResult?.post_ids || [];

      if (!Array.isArray(responseData)) {
        throw new Error("Failed to fetch posts - invalid data format");
      }

      const allowedSocials = new Set(
        (showSocials || []).map((s: unknown) => String(s || "").toLowerCase()).filter(Boolean),
      );
      const platformScoped = allowedSocials.size
        ? responseData.filter((row: any) =>
            allowedSocials.has(String(row?.platform || "").toLowerCase()),
          )
        : responseData;
      const scoped = filterOutProductCatalogPosts(platformScoped, Boolean(isShopLinks));

      // Return paginated structure (matching useLoadTypeContent pattern)
      return {
        data: scoped,
        total_count: totalCount,
        originalDataLength: scoped.length,
        post_ids: allPostIds,
      };
    },
    [
      normalizedQuery,
      processQuery,
      isShopLinks,
      user_id,
      username,
      showSocials,
      order,
      customOrderPostIds,
      normalizedCustomOrder,
      normalizedRemovedPostIds,
      POSTS_PER_PAGE,
    ]
  );

  // Memoize query key
  const queryKey = useMemo(
    () => [
      "paginatedQueryFeed",
      normalizedQuery,
      user_id,
      removedPostIdsKey,
      name,
      order,
      customOrderKey,
      activeFilter,
      (showSocials || []).join(","),
      contentBankRevision,
    ],
    [
      normalizedQuery,
      user_id,
      removedPostIdsKey,
      name,
      order,
      customOrderKey,
      activeFilter,
      showSocials,
      contentBankRevision,
    ]
  );

  const queryResult = useInfiniteQuery(
    queryKey,
    getPaginatedFeed,
    {
    enabled: Boolean(normalizedQuery) && Boolean(user_id),
      getNextPageParam: (lastPage: any, allPages: any) => {
        if (!lastPage || !lastPage.data) return undefined;
        if (lastPage.originalDataLength === 0) return undefined;
        if (lastPage.originalDataLength < POSTS_PER_PAGE) return undefined;
        
        const totalLoaded = allPages.reduce((sum: number, page: any) => {
          return sum + (page?.originalDataLength || page?.data?.length || 0);
        }, 0);

        if (lastPage.total_count && totalLoaded >= lastPage.total_count) {
          return undefined;
        }
        return allPages.length + 1;
      },
      keepPreviousData: true,
      // staleTime: 30000, // ✅ Add 30s stale time to prevent unnecessary refetches
      // cacheTime: 300000, // ✅ Cache for 5 minutes
    ...reactQueryParams(),
    }
  );

  const firstPage = queryResult.data?.pages?.[0];
  const totalCount = firstPage?.total_count || 0;
  const postIds = firstPage?.post_ids || [];

  // ✅ DON'T flatten - return pages structure
  return {
    ...queryResult,
    data: queryResult.data?.pages || [], // Return pages array, not flattened
    length: postIds.length, // Total length from post_ids
    post_ids: postIds,
    total_count: totalCount,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    isPreviousData: queryResult.isPreviousData,
    refetch: queryResult.refetch,
    fetchNextPage: queryResult.fetchNextPage,
    hasNextPage: queryResult.hasNextPage,
    isFetchingNextPage: queryResult.isFetchingNextPage,
  };
}